import { createContext, useContext, useEffect, useRef } from 'react'

export interface SettingsSaveState {
  dirtyCount: number,
  isSubmitting: boolean,
  canSave: boolean,
  onSave: () => void,
  onDiscard: () => void,
}

/**
 * Each tab's form owns its own dirty state, but the bar that saves it is drawn
 * once, at the bottom of the screen, next to the section picker that has to
 * know whether it is there. So the form publishes; the footer subscribes.
 *
 * The alternative — every form rendering its own fixed bar — is what this
 * replaces, and it could not stack: two fixed elements at the same offset with
 * no knowledge of each other just overlap.
 */
const SettingsSaveContext = createContext<(state: SettingsSaveState | null) => void>(() => {});

export const SettingsSaveProvider = SettingsSaveContext.Provider;

/**
 * Publishes this form's save state for as long as it is mounted, and withdraws
 * it on the way out so a tab with nothing to save shows no bar.
 *
 * The handlers go through a ref: they close over render-scope state and change
 * identity every render, and putting them in the dependency array would
 * republish on every keystroke.
 */
export const useSettingsSave = ({
  dirtyCount,
  isSubmitting = false,
  canSave = true,
  onSave,
  onDiscard,
}: {
  dirtyCount: number,
  isSubmitting?: boolean,
  canSave?: boolean,
  onSave: () => void,
  onDiscard: () => void,
}) => {
  const publish = useContext(SettingsSaveContext);
  const handlers = useRef({ onSave, onDiscard });
  handlers.current = { onSave, onDiscard };

  useEffect(() => {
    publish({
      dirtyCount,
      isSubmitting,
      canSave,
      onSave: () => handlers.current.onSave(),
      onDiscard: () => handlers.current.onDiscard(),
    });
    return () => publish(null);
  }, [publish, dirtyCount, isSubmitting, canSave]);
};

export default SettingsSaveContext
